// @ts-strict-ignore
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Layout from 'antd/lib/layout/layout';
import Tabs from 'antd/lib/tabs';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button';
import Flex from 'antd/lib/flex';
import Divider from 'antd/lib/divider';
import Input from 'antd/lib/input';
import Collapse from 'antd/lib/collapse';
import Spin from 'antd/lib/spin';
import { CaretRightOutlined } from '@ant-design/icons';
import {
    DashboardOutlined,
    UnorderedListOutlined,
    ArrowLeftOutlined,
    CheckCircleFilled,
    FieldTimeOutlined,
    ThunderboltOutlined,
} from '@ant-design/icons';
import { useStepRunsSummary } from '@/utils/hooks/useStepRunsSummary';
import { useInfiniteScroll } from '@/utils/hooks/useInfiniteScroll';

import { RootState } from '../../reducers/reducers';
import {
    selectApp,
    selectStepGroupAutomationRuns,
    selectIsStepTimelineDrawerVisible,
    selectReleaseVersion,
    selectStepArchetypeGroupByGroupIndexAndArchetypeGroupIndex,
    selectStepRunsByGroupIndexAndArchetypeGroupIndex,
} from '@/selectors/selectors';
import AutomationTab from '@/components/automations/AutomationTab';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { displayTextForReleaseStepType } from '@/helpers/displayHelpers';
import { flatMap, truncate } from 'lodash';
import { ReleaseStepStatus } from '@/types';
import { StepRunPanel } from './StepRunPanel';
import { setEventsTimelineDrawerVisibility } from '@/actions/ui';
import { updateTimelineEventsFilters } from '@/actions/filters';
import { trackReleaseRouteEvent } from '@/actions/trackEvent';
import * as AnalyticEvents from '@/utils/analytics';
import { EventsTimelineDrawer } from '../timelineEvents/EventsTimelineDrawer';
import { useTimelineEventsPagination } from '@/utils/hooks/useTimelineEventsPagination';
import { globalMessage } from '@/utils/globalMessage';
import { StepStatusAlert } from '../StepStatusHeaderComponent';
import { BatchTransactionBar } from './batchActions';
import { transactionTypesForStepArchetype } from '@/helpers';
import StepGroupChecklistContainer from './StepGroupChecklistContainer';
import { EditDestinationsModal } from './EditDestinationsModal';
import DestinationsBanner from '@/components/releaseOverview/DestinationsBanner';
import { ReleaseStepType } from '@/types';
import { selectRelease } from '@/selectors/selectors';
import { StepRunStatusData } from '@/types/stepRunStatus';
import { STEP_RUNS_SUMMARY_TAB_PARAM } from '@/helpers/routeHelpers';

export interface StepRunsSummaryProps {
    appId: string;
    releaseId: string;
    orgId: string;
    groupIndex: number;
    archetypeGroupIndex: number;
}

enum StepRunsSummaryTab {
    Summary = 'summary',
    Automations = 'automations',
    Checklist = 'checklist',
    Notifications = 'notifications',
}

interface StepRunStatusAlertProps {
    stepRuns: StepRunStatusData[];
}

interface StepStatusSectionProps {
    title: string;
    workflowId: string;
    workflowRunId: string;
    allStepRunIds: string[];
    color: string;
    defaultExpanded: boolean;
    orgId: string;
    appId: string;
    releaseId: string;
    scrollContainerRef: React.RefObject<HTMLDivElement | null>;
}

const StepStatusSection: React.FC<StepStatusSectionProps> = ({
    title,
    workflowId,
    workflowRunId,
    allStepRunIds,
    color,
    defaultExpanded,
    orgId,
    appId,
    releaseId,
    scrollContainerRef,
}) => {
    const {
        stepRuns: steps,
        isLoading,
        hasMore,
        fetchMore,
        isFetching,
    } = useStepRunsSummary({
        orgId,
        workflowId,
        workflowRunId,
        stepRunIds: allStepRunIds,
    });

    const sentinelRef = useInfiniteScroll(hasMore, isFetching, fetchMore, scrollContainerRef);

    return (
        <Collapse
            defaultActiveKey={defaultExpanded ? ['1'] : []}
            ghost
            expandIcon={({ isActive }) => (
                <CaretRightOutlined
                    style={{
                        color: antGrey.grey6,
                        fontSize: 24,
                    }}
                    rotate={isActive ? 90 : 0}
                />
            )}
            style={{
                backgroundColor: runwayColors.white,
                border: 'none',
                margin: 18,
                marginTop: 0,
                padding: 0,
            }}
            items={[
                {
                    key: '1',
                    label: (
                        <Flex align="center" gap={12}>
                            <div
                                style={{
                                    width: 8,
                                    height: 8,
                                    borderRadius: '50%',
                                    backgroundColor: color,
                                }}
                            />
                            <Text strong style={{ fontSize: 16, color: color }}>
                                {title}
                            </Text>
                            <Divider type="vertical" style={{ backgroundColor: antGrey.grey5, margin: 0 }} />
                            <Text style={{ color: antGrey.grey6 }}>{allStepRunIds.length}</Text>
                        </Flex>
                    ),
                    children: (
                        <div style={{ paddingLeft: 20 }}>
                            {steps.map((step) => (
                                <div key={step.id} style={{ paddingBottom: 24 }}>
                                    <StepRunPanel orgId={orgId} appId={appId} releaseId={releaseId} stepRun={step} />
                                </div>
                            ))}
                            {(isLoading || hasMore) && (
                                <div
                                    ref={sentinelRef}
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        padding: '16px 0',
                                        minHeight: 1,
                                    }}
                                >
                                    {(isLoading || isFetching) && <Spin size="small" />}
                                </div>
                            )}
                        </div>
                    ),
                    style: {
                        borderBottom: 'none',
                    },
                },
            ]}
        />
    );
};

const StepRunStatusAlert: React.FC<StepRunStatusAlertProps> = ({ stepRuns }) => {
    const hasBlockedSteps = stepRuns.some((stepRun) => stepRun.status === ReleaseStepStatus.Blocked);
    const hasInProgressSteps = stepRuns.some(
        (stepRun) =>
            stepRun.status === ReleaseStepStatus.Pending || stepRun.status === ReleaseStepStatus.PendingChecklistOnly,
    );
    const allReady = stepRuns.every((stepRun) => stepRun.status === ReleaseStepStatus.Ready);
    const allSkipped = stepRuns.every((stepRun) => stepRun.status === ReleaseStepStatus.Skipped);
    const allInactive = stepRuns.every((stepRun) => stepRun.status === ReleaseStepStatus.Inactive || !stepRun.status);

    const firstStepRun = stepRuns[0];

    let status: ReleaseStepStatus;
    let statusReasonString: string;

    if (hasBlockedSteps) {
        status = ReleaseStepStatus.Blocked;
        statusReasonString = 'One or more steps are blocked.';
    } else if (hasInProgressSteps) {
        status = ReleaseStepStatus.Pending;
        statusReasonString = 'One or more steps are in progress.';
    } else if (allReady) {
        status = ReleaseStepStatus.Ready;
        statusReasonString = firstStepRun?.statusReasonString ?? 'All steps are ready!';
    } else if (allSkipped) {
        status = ReleaseStepStatus.Skipped;
        statusReasonString = 'All steps are skipped.';
    } else if (allInactive) {
        status = ReleaseStepStatus.Inactive;
        statusReasonString = firstStepRun?.statusReasonString ?? 'Waiting for Kickoff to be ready.';
    } else {
        status = ReleaseStepStatus.Inactive;
        statusReasonString = 'Status unknown.';
    }

    return <StepStatusAlert status={status} statusReasonString={statusReasonString} style={{ margin: 36 }} />;
};

interface StepStatusNavigationProps {
    inactiveSteps: StepRunStatusData[];
    readySteps: StepRunStatusData[];
    pendingSteps: StepRunStatusData[];
    blockedSteps: StepRunStatusData[];
    skippedSteps: StepRunStatusData[];
    inactiveRef: React.RefObject<HTMLDivElement>;
    readyRef: React.RefObject<HTMLDivElement>;
    pendingRef: React.RefObject<HTMLDivElement>;
    blockedRef: React.RefObject<HTMLDivElement>;
    skippedRef: React.RefObject<HTMLDivElement>;
}

const StepStatusNavigation: React.FC<StepStatusNavigationProps> = ({
    inactiveSteps,
    readySteps,
    pendingSteps,
    blockedSteps,
    skippedSteps,
    inactiveRef,
    readyRef,
    pendingRef,
    blockedRef,
    skippedRef,
}) => {
    const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
        ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const statusConfig = [
        { steps: inactiveSteps, ref: inactiveRef, color: antGrey.grey6, label: 'inactive' },
        { steps: readySteps, ref: readyRef, color: runwayColors.statusGreen, label: 'ready' },
        { steps: pendingSteps, ref: pendingRef, color: runwayColors.statusYellow, label: 'pending' },
        { steps: blockedSteps, ref: blockedRef, color: runwayColors.statusRed, label: 'blocked' },
        { steps: skippedSteps, ref: skippedRef, color: antGrey.grey6, label: 'skipped' },
    ];

    const visibleStatuses = statusConfig.filter((config) => config.steps.length > 0);

    return (
        <Flex align="center" gap={16} style={{ visibility: visibleStatuses.length > 0 ? 'visible' : 'hidden' }}>
            <Text style={{ fontSize: 14, color: antGrey.grey7 }}>Steps:</Text>
            <Flex gap={8} align="center">
                {visibleStatuses.map((config, index) => (
                    <React.Fragment key={config.label}>
                        <Button
                            type="link"
                            onClick={() => scrollToSection(config.ref)}
                            style={{ padding: 0, height: 'auto', color: config.color, textDecoration: 'underline' }}
                        >
                            {config.steps.length} {config.label}
                        </Button>
                        {index < visibleStatuses.length - 1 && (
                            <Divider type="vertical" style={{ backgroundColor: antGrey.grey5, margin: 0 }} />
                        )}
                    </React.Fragment>
                ))}
            </Flex>
        </Flex>
    );
};

const renderTabLabel = (
    Icon: React.ComponentType<{ style?: React.CSSProperties }>,
    label: string,
    active: boolean,
    extras?: React.ReactNode,
) => (
    <Flex align="center" gap={4}>
        <Icon style={{ color: active ? runwayColors.info : antGrey.grey6 }} />
        <Text style={{ color: active ? antGrey.grey8 : antGrey.grey7 }}>{label}</Text>
        {extras}
    </Flex>
);

const NO_STEP_RUN_STATUS_DATA: StepRunStatusData[] = [];

export const StepRunsSummary: React.FC<StepRunsSummaryProps> = (props) => {
    const { releaseId, groupIndex, archetypeGroupIndex, orgId, appId } = props;

    const location = useLocation();
    const history = useHistory();
    const release = useSelector((state: RootState) => selectRelease(state, releaseId));
    const workflowId = useSelector((state: RootState) => selectApp(state, appId).workflowId);
    const workflowRunId = release.workflowRunId;

    const stepRunStatusDataFromStepGraph =
        useSelector((state: RootState) =>
            selectStepRunsByGroupIndexAndArchetypeGroupIndex(state, releaseId, groupIndex, archetypeGroupIndex),
        ) ?? NO_STEP_RUN_STATUS_DATA;

    const allStepRunIds = stepRunStatusDataFromStepGraph.map((sr) => sr.stepRunId);
    const allDestinationSummaries = release.destinationSummaries || [];
    const destinationSummariesByStepRunId = React.useMemo(() => {
        return allDestinationSummaries.reduce(
            (acc, dest) => {
                const matchingStepRuns = stepRunStatusDataFromStepGraph.filter((sr) =>
                    sr.destinationStepConfigIds?.includes(dest.stepConfigId),
                );
                for (const sr of matchingStepRuns) {
                    if (!acc[sr.stepRunId]) {
                        acc[sr.stepRunId] = [];
                    }
                    acc[sr.stepRunId].push(dest);
                }
                return acc;
            },
            {} as Record<string, typeof allDestinationSummaries>,
        );
    }, [allDestinationSummaries, stepRunStatusDataFromStepGraph]);

    const allPlatforms = flatMap(
        stepRunStatusDataFromStepGraph,
        (stepRun) => destinationSummariesByStepRunId[stepRun.stepRunId]?.map((dest) => dest.platform) ?? [],
    );
    const allAppStoreTypes = flatMap(
        stepRunStatusDataFromStepGraph,
        (stepRun) => destinationSummariesByStepRunId[stepRun.stepRunId]?.map((dest) => dest.appStoreType) ?? [],
    );

    const releaseVersion = useSelector((state: RootState) => selectReleaseVersion(state, releaseId));
    const archetypeGroup = useSelector((state: RootState) =>
        selectStepArchetypeGroupByGroupIndexAndArchetypeGroupIndex(state, releaseId, groupIndex, archetypeGroupIndex),
    );

    const stepGroupAutomationRuns = useSelector((state: RootState) =>
        selectStepGroupAutomationRuns(state, release.workflowRunId, archetypeGroup?.archetype),
    );

    // The tab lives in the URL so the sidebar can link straight to one (e.g. its checklist tag)
    // and can tell which tab is showing.
    const queryTab = new URLSearchParams(location.search).get(STEP_RUNS_SUMMARY_TAB_PARAM);
    const activeTab = Object.values(StepRunsSummaryTab).find((tab) => tab === queryTab) ?? StepRunsSummaryTab.Summary;
    const setActiveTab = React.useCallback(
        (tab: StepRunsSummaryTab) => {
            const params = new URLSearchParams(location.search);
            params.set(STEP_RUNS_SUMMARY_TAB_PARAM, tab);
            history.replace({ pathname: location.pathname, search: params.toString() });
        },
        [history, location.pathname, location.search],
    );

    React.useEffect(() => {
        // Fall back to summary if the step-group automations tab is no longer available
        if (activeTab === StepRunsSummaryTab.Automations && stepGroupAutomationRuns.length === 0) {
            setActiveTab(StepRunsSummaryTab.Summary);
        }
    }, [activeTab, stepGroupAutomationRuns.length, setActiveTab]);

    const [searchQuery, setSearchQuery] = React.useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = React.useState('');
    const [isEditDestinationsModalVisible, setIsEditDestinationsModalVisible] = React.useState(false);

    React.useEffect(() => {
        const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 300);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    const inactiveRef = React.useRef<HTMLDivElement>(null);
    const readyRef = React.useRef<HTMLDivElement>(null);
    const pendingRef = React.useRef<HTMLDivElement>(null);
    const blockedRef = React.useRef<HTMLDivElement>(null);
    const skippedRef = React.useRef<HTMLDivElement>(null);
    const scrollContainerRef = React.useRef<HTMLDivElement>(null);

    const groupName =
        archetypeGroup?.name ||
        displayTextForReleaseStepType(archetypeGroup?.archetype, allPlatforms, allAppStoreTypes, true);

    const filteredStepRuns = React.useMemo(() => {
        if (!debouncedSearchQuery.trim()) {
            return stepRunStatusDataFromStepGraph;
        }

        const query = debouncedSearchQuery.toLowerCase().trim();
        return stepRunStatusDataFromStepGraph.filter((sr) => {
            const destinations = destinationSummariesByStepRunId[sr.stepRunId] ?? [];
            const stepName = (
                sr.name ||
                displayTextForReleaseStepType(
                    sr.type,
                    destinations.map((d) => d.platform),
                    destinations.map((d) => d.appStoreType),
                    true,
                )
            ).toLowerCase();
            const stepId = sr.stepRunId.toLowerCase();
            return stepName.includes(query) || stepId.includes(query);
        });
    }, [stepRunStatusDataFromStepGraph, destinationSummariesByStepRunId, debouncedSearchQuery]);

    const inactiveSteps = filteredStepRuns.filter((sr) => sr.status === ReleaseStepStatus.Inactive || !sr.status);
    const readySteps = filteredStepRuns.filter((sr) => sr.status === ReleaseStepStatus.Ready);
    const pendingSteps = filteredStepRuns.filter(
        (sr) => sr.status === ReleaseStepStatus.Pending || sr.status === ReleaseStepStatus.PendingChecklistOnly,
    );
    const blockedSteps = filteredStepRuns.filter((sr) => sr.status === ReleaseStepStatus.Blocked);
    const skippedSteps = filteredStepRuns.filter((sr) => sr.status === ReleaseStepStatus.Skipped);

    const dispatch = useDispatch();
    const stepRunDisplayName = React.useMemo(() => {
        const fullName = filteredStepRuns
            .map((sr) => {
                const destinations = destinationSummariesByStepRunId[sr.stepRunId] ?? [];
                return (
                    sr.name ||
                    displayTextForReleaseStepType(
                        sr.type,
                        destinations.map((d) => d.platform),
                        destinations.map((d) => d.appStoreType),
                        true,
                    )
                );
            })
            .join(', ');

        return truncate(fullName, { length: 200 });
    }, [destinationSummariesByStepRunId, filteredStepRuns]);

    const isDrawerVisible = useSelector((state: RootState) =>
        selectIsStepTimelineDrawerVisible(state, stepRunDisplayName),
    );

    const {
        events,
        error,
        loadMore: loadMoreEvents,
        isLoading: isLoadingEvents,
        isLoadingMore: isLoadingMoreEvents,
        paginationInfo,
    } = useTimelineEventsPagination({
        orgId,
        appId,
        workflowId,
        workflowRunId,
        releaseId,
        releaseStepType: archetypeGroup?.archetype,
        stepRunIds: allStepRunIds,
        initialLoad: true,
    });
    React.useEffect(() => {
        dispatch(
            trackReleaseRouteEvent({
                orgId: orgId,
                appId: appId,
                releaseId: releaseId,
                eventName: AnalyticEvents.Event.ViewStepRunsSummary,
                stepArchetype: archetypeGroup?.archetype,
            }),
        );
    }, []);

    React.useEffect(() => {
        if (error) {
            globalMessage.error('An error occurred when fetching step run timeline events.');
        }
    }, [error]);

    const handleDrawerClose = React.useCallback(() => {
        dispatch(setEventsTimelineDrawerVisibility(null, false));
        dispatch(updateTimelineEventsFilters({ filters: [] }));
    }, [dispatch]);

    const transactionTypes = React.useMemo(() => {
        if (!archetypeGroup) {
            return [];
        }

        return transactionTypesForStepArchetype(archetypeGroup.archetype);
    }, [archetypeGroup]);

    const pendingChecklistItemsCount = React.useMemo(
        () => stepRunStatusDataFromStepGraph.reduce((count, s) => count + s.pendingChecklistItemCount, 0),
        [stepRunStatusDataFromStepGraph],
    );

    const totalChecklistItemsCount = React.useMemo(
        () => stepRunStatusDataFromStepGraph.reduce((count, s) => count + s.totalChecklistItemCount, 0),
        [stepRunStatusDataFromStepGraph],
    );

    const stepRunStatusDataWithChecklistItems = React.useMemo(
        () => stepRunStatusDataFromStepGraph.filter((s) => s.totalChecklistItemCount > 0),
        [stepRunStatusDataFromStepGraph],
    );

    const completedChecklistItemsCount = totalChecklistItemsCount - pendingChecklistItemsCount;
    const allChecklistItemsCompleted = completedChecklistItemsCount === totalChecklistItemsCount;

    return (
        <>
            <Layout
                style={{
                    minWidth: 1000,
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <div ref={scrollContainerRef} style={{ overflowY: 'auto', flex: 1 }}>
                    <StepRunStatusAlert stepRuns={stepRunStatusDataFromStepGraph} />

                    <div style={{ paddingLeft: 36, paddingRight: 36, marginBottom: 16 }}>
                        {archetypeGroup?.archetype === ReleaseStepType.Submission &&
                            allDestinationSummaries?.length > 0 && (
                                <DestinationsBanner
                                    destinationSummaries={allDestinationSummaries}
                                    showEditButton={true}
                                    onEditClick={() => setIsEditDestinationsModalVisible(true)}
                                />
                            )}
                    </div>

                    <div style={{ paddingLeft: 36, paddingRight: 36 }}>
                        <Flex align="center" gap={8}>
                            <Text
                                style={{
                                    fontSize: 18,
                                    fontWeight: 600,
                                    letterSpacing: '0.1em',
                                    color: antGrey.grey7,
                                    textTransform: 'uppercase',
                                    marginRight: 36,
                                }}
                            >
                                {groupName}
                            </Text>
                            <Tabs
                                activeKey={activeTab}
                                onChange={(activeKey) => setActiveTab(activeKey as StepRunsSummaryTab)}
                                type="card"
                                tabBarStyle={{ marginBottom: 0 }}
                                items={[
                                    {
                                        key: StepRunsSummaryTab.Summary,
                                        label: renderTabLabel(
                                            DashboardOutlined,
                                            'Summary',
                                            activeTab === StepRunsSummaryTab.Summary,
                                        ),
                                    },
                                    ...(stepGroupAutomationRuns.length > 0 ?
                                        [
                                            {
                                                key: StepRunsSummaryTab.Automations,
                                                label: renderTabLabel(
                                                    ThunderboltOutlined,
                                                    'Automations',
                                                    activeTab === StepRunsSummaryTab.Automations,
                                                ),
                                            },
                                        ]
                                    :   []),
                                    {
                                        key: StepRunsSummaryTab.Checklist,
                                        label: renderTabLabel(
                                            UnorderedListOutlined,
                                            'Checklist',
                                            activeTab === StepRunsSummaryTab.Checklist,
                                            <>
                                                <Text
                                                    style={{
                                                        color:
                                                            allChecklistItemsCompleted ?
                                                                runwayColors.statusGreen
                                                            :   runwayColors.statusYellow,
                                                    }}
                                                >
                                                    {completedChecklistItemsCount} of {totalChecklistItemsCount}
                                                </Text>
                                                {allChecklistItemsCompleted && (
                                                    <CheckCircleFilled style={{ color: runwayColors.statusGreen }} />
                                                )}
                                            </>,
                                        ),
                                    },
                                    //TODO: hidden for  now
                                    // {
                                    //     key: StepRunsSummaryTab.Notifications,
                                    //     label: (
                                    //         <Flex align="center" gap={4}>
                                    //             <BellOutlined
                                    //                 style={{
                                    //                     color:
                                    //                         activeTab === StepRunsSummaryTab.Notifications ?
                                    //                             runwayColors.info
                                    //                         :   antGrey.grey6,
                                    //                 }}
                                    //             />
                                    //             <Text
                                    //                 style={{
                                    //                     color:
                                    //                         activeTab === StepRunsSummaryTab.Notifications ?
                                    //                             antGrey.grey8
                                    //                         :   antGrey.grey7,
                                    //                 }}
                                    //             >
                                    //                 Notifications
                                    //             </Text>
                                    //         </Flex>
                                    //     ),
                                    // },
                                ]}
                            />
                            <Flex align="center" gap={8}>
                                <FieldTimeOutlined style={{ color: antGrey.grey7 }} />
                                <Text
                                    style={{
                                        fontSize: '13px',
                                        color: antGrey.grey7,
                                        letterSpacing: '0.1em',
                                        textTransform: 'uppercase',
                                        fontWeight: 400,
                                    }}
                                >
                                    TIMELINE
                                </Text>
                                <Divider type="vertical" style={{ backgroundColor: antGrey.grey5 }} />
                                <Button
                                    type="link"
                                    style={{ padding: 0, fontSize: '13px' }}
                                    onClick={() => {
                                        dispatch(setEventsTimelineDrawerVisibility(stepRunDisplayName, true));
                                    }}
                                >
                                    View
                                    <ArrowLeftOutlined />
                                </Button>
                            </Flex>
                        </Flex>
                    </div>
                    {activeTab === StepRunsSummaryTab.Summary && (
                        <>
                            <div style={{ padding: '16px 36px', paddingTop: 36 }}>
                                <Flex align="center" justify="space-between">
                                    <StepStatusNavigation
                                        inactiveSteps={inactiveSteps}
                                        readySteps={readySteps}
                                        pendingSteps={pendingSteps}
                                        blockedSteps={blockedSteps}
                                        skippedSteps={skippedSteps}
                                        inactiveRef={inactiveRef}
                                        readyRef={readyRef}
                                        pendingRef={pendingRef}
                                        blockedRef={blockedRef}
                                        skippedRef={skippedRef}
                                    />
                                    <Input.Search
                                        placeholder="Search by name or ID"
                                        style={{ width: 240 }}
                                        allowClear
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                    />
                                </Flex>
                            </div>

                            <Divider style={{ margin: 0, marginBottom: 18 }} />
                            <div>
                                {blockedSteps.length > 0 && (
                                    <div ref={blockedRef}>
                                        <StepStatusSection
                                            title="Blocked"
                                            workflowId={workflowId}
                                            workflowRunId={workflowRunId}
                                            allStepRunIds={blockedSteps.map((s) => s.stepRunId)}
                                            color={runwayColors.statusRed}
                                            defaultExpanded={true}
                                            orgId={orgId}
                                            appId={appId}
                                            releaseId={releaseId}
                                            scrollContainerRef={scrollContainerRef}
                                        />
                                    </div>
                                )}
                                {pendingSteps.length > 0 && (
                                    <div ref={pendingRef}>
                                        <StepStatusSection
                                            title="Pending"
                                            workflowId={workflowId}
                                            workflowRunId={workflowRunId}
                                            allStepRunIds={pendingSteps.map((s) => s.stepRunId)}
                                            color={runwayColors.statusYellow}
                                            defaultExpanded={blockedSteps.length === 0}
                                            orgId={orgId}
                                            appId={appId}
                                            releaseId={releaseId}
                                            scrollContainerRef={scrollContainerRef}
                                        />
                                    </div>
                                )}
                                {readySteps.length > 0 && (
                                    <div ref={readyRef}>
                                        <StepStatusSection
                                            title="Ready"
                                            workflowId={workflowId}
                                            workflowRunId={workflowRunId}
                                            allStepRunIds={readySteps.map((s) => s.stepRunId)}
                                            color={runwayColors.statusGreen}
                                            defaultExpanded={blockedSteps.length === 0 && pendingSteps.length === 0}
                                            orgId={orgId}
                                            appId={appId}
                                            releaseId={releaseId}
                                            scrollContainerRef={scrollContainerRef}
                                        />
                                    </div>
                                )}
                                {inactiveSteps.length > 0 && (
                                    <div ref={inactiveRef}>
                                        <StepStatusSection
                                            title="Inactive"
                                            workflowId={workflowId}
                                            workflowRunId={workflowRunId}
                                            allStepRunIds={inactiveSteps.map((s) => s.stepRunId)}
                                            color={antGrey.grey6}
                                            defaultExpanded={false}
                                            orgId={orgId}
                                            appId={appId}
                                            releaseId={releaseId}
                                            scrollContainerRef={scrollContainerRef}
                                        />
                                    </div>
                                )}
                                {skippedSteps.length > 0 && (
                                    <div ref={skippedRef}>
                                        <StepStatusSection
                                            title="Skipped"
                                            workflowId={workflowId}
                                            workflowRunId={workflowRunId}
                                            allStepRunIds={skippedSteps.map((s) => s.stepRunId)}
                                            color={antGrey.grey6}
                                            defaultExpanded={false}
                                            orgId={orgId}
                                            appId={appId}
                                            releaseId={releaseId}
                                            scrollContainerRef={scrollContainerRef}
                                        />
                                    </div>
                                )}
                            </div>
                            <div style={{ paddingBottom: 56 }} />
                        </>
                    )}
                    {activeTab === StepRunsSummaryTab.Automations && (
                        <div style={{ padding: '16px 36px', paddingTop: 36 }}>
                            <AutomationTab
                                orgId={orgId}
                                appId={appId}
                                releaseId={releaseId}
                                releaseStepType={archetypeGroup?.archetype ?? null}
                                isReleaseStep={true}
                                automationRuns={stepGroupAutomationRuns}
                            />
                        </div>
                    )}
                    {activeTab === StepRunsSummaryTab.Checklist && (
                        <div style={{ padding: '16px 36px', paddingTop: 36 }}>
                            <StepGroupChecklistContainer
                                appId={appId}
                                orgId={orgId}
                                workflowId={workflowId}
                                workflowRunId={workflowRunId}
                                releaseId={releaseId}
                                releaseStepType={archetypeGroup?.archetype}
                                stepRunStatusDataWithChecklistItems={stepRunStatusDataWithChecklistItems}
                                scrollContainerRef={scrollContainerRef}
                            />
                        </div>
                    )}
                </div>
                {transactionTypes.length > 0 && (
                    <BatchTransactionBar
                        orgId={orgId}
                        appId={appId}
                        releaseId={releaseId}
                        transactionTypes={transactionTypes}
                        stepRunStatusData={stepRunStatusDataFromStepGraph}
                    />
                )}
            </Layout>
            <EventsTimelineDrawer
                events={events || []}
                open={isDrawerVisible}
                onClose={handleDrawerClose}
                orgId={orgId}
                appId={appId}
                version={releaseVersion}
                releaseStepDisplayName={stepRunDisplayName}
                loadMore={loadMoreEvents}
                isLoading={isLoadingMoreEvents || isLoadingEvents}
                hasMore={paginationInfo?.hasMore}
            />
            <EditDestinationsModal
                visible={isEditDestinationsModalVisible}
                onClose={() => setIsEditDestinationsModalVisible(false)}
                appId={appId}
                orgId={orgId}
                releaseId={releaseId}
            />
        </>
    );
};
